import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Delivery, DeliveryStatus, DeliveryStatusAux } from 'src/app/models/delivery.schema';
import { Partner } from 'src/app/models/partner.schema';
import { ShopifySale } from 'src/app/models/shopify-sale.schema';
import { CommanUsesService } from 'src/app/services/comman-uses.service';
import { CustomPaymentsService } from 'src/app/services/custom-payments.service';
import { SpinnerService } from 'src/app/services/spinner.service';

@Component({
  selector: 'app-sale-tracking',
  templateUrl: './sale-tracking.component.html',
  styleUrls: ['./sale-tracking.component.scss']
})
export class SaleTrackingComponent implements OnInit {

  ddtNumber: string;
  salePlatform: string;
  orderId: string;
  deliveries: Delivery[] = [];
  shopifyOrder: any;
  currentStatus: string;
  statuses: string[] = [];
  steps: any[] = [];
  showingCurrentStatus: string;
  currentStepIndex: number = 0; 
  isNotDelivered: boolean = false;
  dbOrder: ShopifySale;
  totalPrice: number = 0;
  taxPrice: number = 0;
  shippingPrice: number = 0;
  subtotal: number = 0;
  orderedStatuses = [
    'created',
    'assigned',
    'delivering',
    'delivered',
    'notdelivered',
    'canceled',
  ];
  basicInfo: {
    id: number,
    deliveryDate: any,
    deliveryTime: string,
    address: string,
    firstName: string,
    lastName: string,
    intercom: string,
    phone: string,
    notes: string,
  }
  partner: Partner;

  constructor(
    private spinnerService: SpinnerService,
    private route: ActivatedRoute,
    private commanUsesService: CommanUsesService,
    private customPaymentsService: CustomPaymentsService,
  ) { }

  async ngOnInit() {
    try {
      this.spinnerService.show();
      this.route.queryParams.subscribe(params => {
        this.ddtNumber = params['sale_id'];
        this.salePlatform = params['platform'];
        this.orderId = params['id'];
      });

      const result: { realOrderNumber: number } = await this.customPaymentsService.findShopifyOrderId(this.ddtNumber, this.salePlatform);
      if(!result?.realOrderNumber) {
        this.commanUsesService.showSnackBar(`CAN_NOT_FIND_ORDER_DETAILS_WITH_ID_${this.ddtNumber}`);
        return;
      }

      const promiseResult: { shopify_order, deliveries: Delivery[], db_order} = await this.customPaymentsService.findShopifyOrderDeliveries(String(result.realOrderNumber), this.salePlatform);
      /*
      if(!promiseResult.deliveries.length) {
        this.commanUsesService.showSnackBar('NO_DELIVERIES_CREATED_YET_FOR_THIS_SALE');
        return;
      } 
      */

      this.deliveries = promiseResult.deliveries || [];
      this.partner = this.deliveries[0]?.partner || undefined;
      this.shopifyOrder = promiseResult.shopify_order || undefined;
      this.dbOrder = promiseResult.db_order || undefined;

      /*
      console.log("ddtNumber", this.ddtNumber);
      console.log("salePlatform", this.salePlatform);
      console.log("promiseResult", promiseResult);
      console.log("this.deliveries", this.deliveries);
      console.log("this.shopifyOrder", this.shopifyOrder);
      */

      this.maintainSaleStatus();
      this.maintainBasicInformation();
      this.maintainTotals();
    } catch (error) {
      const errorMessage = error?.error?.message || error?.message;
      this.commanUsesService.showSnackBar(errorMessage);
    } finally {
      this.spinnerService.hide();
    }
  }

  async maintainBasicInformation() {
    this.basicInfo = {
      id: this.dbOrder.id,
      deliveryDate: this.dbOrder?.deliveryDate,
      deliveryTime: this.dbOrder?.deliveryTime,
      address: this.dbOrder?.address,
      firstName: this.dbOrder?.firstName,
      lastName: this.dbOrder?.lastName,
      intercom: this.dbOrder?.intercom,
      phone: this.dbOrder?.phone,
      notes: this.dbOrder?.notes,
    }
  }

  async maintainTotals() {
    try {
      this.subtotal = this.shopifyOrder?.total_line_items_price || 0;
      this.shippingPrice = this.shopifyOrder?.total_shipping_price_set?.shop_money?.amount || 0;
      this.totalPrice = this.shopifyOrder?.total_price || 0;
    } catch (error) {
      console.error('Error maintainTotals:', error);
    }
  }

  async maintainSaleStatus() {
    try {
      const deliveries = this.deliveries || [];

      if (!deliveries.length) {
        this.currentStatus = DeliveryStatus.created.toLowerCase();
        this.showingCurrentStatus = DeliveryStatusAux.trackingAux[this.currentStatus]?.ln?.it || '';
        this.statuses = [];
        this.isNotDelivered = false;
        this.steps = this.getSteps();
        this.currentStepIndex = 0;
        const currentStep = this.steps.find(step => step.status === this.currentStatus);
        let targetIndex = currentStep ? this.steps.indexOf(currentStep) + 1 : 0;

        if (currentStep?.status === 'created' && targetIndex < this.steps.length) {
          targetIndex++;
        }

        if (targetIndex > 0) {
          this.currentStepIndex = 0;
          setTimeout(() => this.animateStatusChange(targetIndex), 50);
        } else {
          this.currentStepIndex = 0;
        }
        return;
      }

      const orderedStatuses = [
        DeliveryStatus.created.toLowerCase(),
        DeliveryStatus.accepted.toLowerCase(),
        DeliveryStatus.assigned.toLowerCase(),
        DeliveryStatus.delivering.toLowerCase(),
        DeliveryStatus.delivered.toLowerCase(),
        DeliveryStatus.notDelivered.toLowerCase(),
        DeliveryStatus.canceled.toLowerCase(),
      ];

      const allStatuses = deliveries
        .map((d: any) => d.status?.toLowerCase())
        .filter((s: string) => orderedStatuses.includes(s));

      if (!allStatuses.length) {
        this.currentStatus = DeliveryStatus.created.toLowerCase();
        return;
      }

      let minIndex = orderedStatuses.length;

      allStatuses.forEach((status: string) => {
        const index = orderedStatuses.indexOf(status);

        const effectiveIndex =
          status === DeliveryStatus.accepted.toLowerCase()
            ? orderedStatuses.indexOf(DeliveryStatus.created.toLowerCase())
            : index;

        if (effectiveIndex !== -1 && effectiveIndex < minIndex) {
          minIndex = effectiveIndex;
        }
      });

      this.currentStatus = orderedStatuses[minIndex];
      this.showingCurrentStatus = DeliveryStatusAux.trackingAux[this.currentStatus]?.ln?.it || '';

      this.statuses = this.getStatuses(deliveries);
      this.isNotDelivered = this.checkIfNotDelivered();
      this.steps = this.getSteps();

      const currentStep = this.steps.find(step => step.status === this.currentStatus);
      let targetIndex = currentStep ? this.steps.indexOf(currentStep) + 1 : 0;

      if (currentStep?.status === 'created' && targetIndex < this.steps.length) {
        targetIndex++;
      }

      if (targetIndex > 0) {
        this.currentStepIndex = 0;
        setTimeout(() => this.animateStatusChange(targetIndex), 50);
      } else {
        this.currentStepIndex = 0;
      }

    } catch (error) {
      console.error('Error maintaining sale status:', error);
    }
  }

  getStatuses(deliveries: any[]): string[] {
    return deliveries
      .map(d => d.status?.toLowerCase())
      .filter(status => this.orderedStatuses.includes(status)) || [];
  }

  getSteps(): any[] {
    let baseSteps = [
      { label: 'Ordine confermato', icon: 'fas fa-check-circle', status: 'created' },
      { label: 'In creazione dall’Artista', icon: 'fas fa-truck-pickup', status: 'assigned' },
      { label: 'Sulla strada', icon: 'fas fa-shipping-fast', status: 'delivering' },
    ];

    if (this.currentStatus !== 'canceled') {
      if (this.isNotDelivered) {
        baseSteps.push({ label: 'Non Consegnato', icon: 'fas fa-times-circle', status: 'notdelivered' });
      } else {
        baseSteps.push({ label: 'Consegnato', icon: 'fas fa-box', status: 'delivered' });
      }
    }

    if (this.currentStatus === 'canceled') {
      baseSteps.push({ label: 'Annullato', icon: 'fas fa-times-circle', status: 'canceled' });
    }

    return baseSteps;
  }

  checkIfNotDelivered(): boolean {
    return this.currentStatus === DeliveryStatus.notDelivered.toLowerCase();
  }

  animateStatusChange(targetIndex: number) {
    if (this.currentStepIndex >= targetIndex) return;

    const stepDelay = 800; 
    const interval = setInterval(() => {
      this.currentStepIndex++;

      if (this.currentStepIndex >= targetIndex) {
        clearInterval(interval);
      }
    }, stepDelay);
  }

  isStepActive(index: number): boolean {
    return index < this.currentStepIndex;
  }

  isStepNotDelivered(index: number): boolean {
    return (index < this.currentStepIndex && (this.isNotDelivered || this.currentStatus === 'canceled'));
  }

  getOrderStatus(deliveries: any[]): string {
    if (!deliveries || deliveries.length === 0)
      return DeliveryStatus.created.toLowerCase();

    const statuses = deliveries.map(d => d.status?.toLowerCase());

    if (statuses.includes(DeliveryStatus.notDelivered.toLowerCase()))
      return DeliveryStatus.notDelivered.toLowerCase();
    if (statuses.includes(DeliveryStatus.delivered.toLowerCase()))
      return DeliveryStatus.delivered.toLowerCase();
    if (statuses.includes(DeliveryStatus.delivering.toLowerCase()))
      return DeliveryStatus.delivering.toLowerCase();
    if (statuses.includes(DeliveryStatus.assigned.toLowerCase()))
      return DeliveryStatus.assigned.toLowerCase();

    return DeliveryStatus.created.toLowerCase();
  }

}
